import { Box, Heading, RadioGroup } from '@wix/design-system'
import {
  ChoiceQuestionContentComponent,
  ChoiceQuestionOption,
  useContent,
  useNext,
  usePageLoading,
} from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { createEnterpriseLead } from '../../../api/enterpriseLead'
import { usePageLogger } from '../../../bi/mfi'
import { LARGE_DESKTOP } from '../../../breakpoints'
import { withMonitoring } from '../../../monitoring/withMonitoring'
import { BrandedAppLogoWithBack } from '../../reusable/BrandedAppLogoWithBack'
import { Button } from '../../reusable/Button'
import { SkipButton } from '../../reusable/SkipButton'
import { VideoLayout as PageLayout } from '../../reusable/VideoLayout/VideoLayout'
import { VisualBox } from '../../reusable/VisualBox/VisualBox'
import s from './BrandedAppWelcome.scss'

type Option = ChoiceQuestionOption<{
  title: string
  src: string
  description: string
  createEnterpriseLead: boolean
  visualType: string
}>

export type TBrandedAppWelcome = {
  skipPosition: string
  skip: string
  next: string
  backgroundColor: string
  question: ChoiceQuestionContentComponent<
    {
      title: string
      subtitle: string
      src: string
      visualType: string
    },
    {
      title: string
      description: string
      src: string
      createEnterpriseLead: boolean
      visualType: string
    }
  >
  legalDisclaimer: string
}

export const BrandedAppWelcome: FC = () => {
  const [clickedButton, setClickedButton] = useState<'next' | 'skip'>()
  const { content } = useContent<TBrandedAppWelcome>()
  const [pageLoading] = usePageLoading()

  const { skip, skipPosition, next: nextBtn, backgroundColor, question } = content
  const { questionKey, content: questionContent, options: questionOptions } = question
  const optionWithEnterpriseLead = content.question.options.find(option => option.createEnterpriseLead)

  const [selectedOption, setSelectedOption] = useState<Option>({
    optionKey: '',
    description: '',
    title: questionContent.title,
    src: questionContent.src,
    createEnterpriseLead: false,
    ...optionWithEnterpriseLead,
    visualType: questionContent.visualType,
  })

  const [hoveredOption, setHoveredOption] = useState<Option>()

  const next = useNext()

  const pageLogger = usePageLogger()

  const isLargeDesktopScreen = useMediaQuery(LARGE_DESKTOP)

  const handleChange = (value?: number | string) => {
    const option = questionOptions.find(option => option.optionKey === value)
    if (!option) {
      return
    }
    void pageLogger.clickAnswer({ questionKey, buttonName: String(value) })
    setSelectedOption(option)
  }

  const handleSkip = () => {
    if (clickedButton !== undefined || pageLoading) {
      return
    }
    setClickedButton('skip')
    void pageLogger.generalAction({ action: 'skip' })
    next({
      [questionKey]: {
        skipped: true,
      },
    })
  }

  const handleMouseEnter = (option: Option) => {
    setHoveredOption(option)
  }

  const handleMouseLeave = () => {
    setHoveredOption(undefined)
  }

  const handleNext = async () => {
    if (clickedButton !== undefined || pageLoading) {
      return
    }

    setClickedButton('next')
    void pageLogger.answer({ answer: selectedOption?.optionKey || '' })
    if (selectedOption?.createEnterpriseLead) {
      await withMonitoring(
        () =>
          createEnterpriseLead({
            source: 'Enterprise intro flow v3',
            ref: 'https://www.wix.com/intro',
          }),
        'Enterprise-createLead',
      )
    }
    next({
      [questionKey]: { values: [selectedOption?.optionKey ?? ''] },
    })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const mainContentNode = (
    <Box direction="vertical" verticalAlign="middle" className={s.container}>
      <Box className={s.subtitleContainer}>
        <Heading className={s.subtitle}>{questionContent.subtitle}</Heading>
      </Box>
      <Box className={s.titleContainer}>
        <Heading className={s.title}>{questionContent.title}</Heading>
      </Box>
      <RadioGroup
        value={selectedOption?.optionKey}
        onChange={handleChange}
        selectionArea="always"
        selectionAreaSkin="outlined"
        className={s.radio}
      >
        {questionOptions.map(option => (
          <RadioGroup.Radio
            key={option.optionKey}
            value={option.optionKey}
            onMouseEnter={() => handleMouseEnter(option)}
            onMouseLeave={handleMouseLeave}
          >
            {option.title}
          </RadioGroup.Radio>
        ))}
      </RadioGroup>
      <Box marginTop="42px">
        <Button
          isLoading={clickedButton === 'next'}
          onClick={handleNext}
          label={nextBtn}
          disabled={selectedOption.optionKey === ''}
          size={isLargeDesktopScreen ? 'large' : 'medium'}
          className={!selectedOption ? s.hidden : undefined}
        />
      </Box>
    </Box>
  )

  const VisualNode = (
    <Box position="relative" width="100%" backgroundColor={backgroundColor} align="center" verticalAlign="middle">
      <Box className={`${s.videoWrapper} ${s.visible}`}>
        <VisualBox
          visualType={hoveredOption?.visualType ?? selectedOption.visualType}
          description={hoveredOption?.description ?? selectedOption.description}
          src={hoveredOption?.src ?? selectedOption.src}
        />
      </Box>
    </Box>
  )
  return (
    <Box height="100vh" overflow="hidden">
      <BrandedAppLogoWithBack />
      <PageLayout mainContent={mainContentNode} video={VisualNode} />
      {skip && <SkipButton skipButton={skip} position={skipPosition} onClick={handleSkip} />}
    </Box>
  )
}
